// @flow
import {
  AppBar,
  Box,
  Button,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Input,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Card,
  CardContent,
  Grid,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Build as DraftIcon,
  HourglassEmpty as ValidatingIcon,
  NavigateNext as NextIcon,
  WhatsApp as SupportIcon,
  Work as ProcessingIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Error as ErrorIcon,
} from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory } from 'react-router-dom';
import routes from '../routes';
import ProcessRenewalPaymentData from '../component/ProcessRenewalPaymentData';
import ProcessBillingData from '../component/ProcessBillingData';
import { AuthContext, setAuthToken } from '../context/auth';
import { toSentence } from '../util/string';
import { whatsappURL } from '../util/vendor';
import ApiResponse from '../model/ApiResponse';
import { type PaymentProps } from '../model/props';
import SignProcessConfig from '../model/SignProcessConfig';
import RenewalSignProcess from '../model/RenewalSignProcess';

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(2),
  },
  appBarContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  logo: {
    height: 32,
    marginRight: theme.spacing(2),
  },
  brandContainer: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
  },
  brandText: {
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  poweredBy: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
    marginLeft: 'auto',
  },
  draft: {
    color: theme.palette.secondary['900'],
  },
  validating: {
    color: theme.palette.grey['700'],
  },
  processing: {
    color: theme.palette.grey['900'],
  },
  grey: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error['600'],
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(14),
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  bottomButton: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  nextStepButton: {
    background: 'linear-gradient(45deg, #2196F3 30%, #021c49 90%)',
    border: 0,
    borderRadius: 25,
    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
    color: 'white',
    height: 56,
    padding: '0 30px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    textTransform: 'none',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      background: 'linear-gradient(45deg, #1976D2 30%, #021c49 90%)',
      boxShadow: '0 6px 10px 4px rgba(33, 203, 243, .3)',
      transform: 'translateY(-2px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  nextStepContainer: {
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    textAlign: 'center',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e0e0e0',
  },
  nextStepText: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  nextStepSubtext: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
  },
  pulseAnimation: {
    animation: '$pulse 2s infinite',
  },
  discardedCard: {
    backgroundColor: '#ffebee', // rojo muy suave
    borderLeft: '5px solid #e53935', // rojo fuerte
    borderRadius: 8,
    padding: '20px',
    marginTop: 16,
    animation: '$fadeIn 0.3s ease-out',
  },

  discardedIcon: {
    fontSize: 48,
    color: '#d32f2f',
    marginBottom: 10,
  },

  discardedTitle: {
    fontWeight: 700,
    color: '#c62828',
    marginBottom: 8,
  },

  discardedDescription: {
    color: '#555',
    marginBottom: 16,
  },

  discardedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#ffcdd2',
    color: '#b71c1c',
    padding: '6px 12px',
    borderRadius: 20,
    fontWeight: 600,
    gap: 6,
  },
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(33, 150, 243, 0.7)',
    },
    '70%': {
      boxShadow: '0 0 0 10px rgba(33, 150, 243, 0)',
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(33, 150, 243, 0)',
    },
  },
  supportContainer: {
    background: 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
    margin: theme.spacing(1, 0),
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    border: '1px solid #c8e6c9',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
    },
  },
  supportButton: {
    background: 'linear-gradient(45deg, #4CAF50 30%, #2E7D32 90%)',
    color: 'white',
    borderRadius: 20,
    padding: theme.spacing(1.5, 3),
    fontSize: '1rem',
    fontWeight: '500',
    textTransform: 'none',
    boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      background: 'linear-gradient(45deg, #45a049 30%, #1B5E20 90%)',
      boxShadow: '0 4px 8px rgba(76, 175, 80, 0.4)',
      transform: 'translateY(-1px)',
    },
  },
  supportIcon: {
    color: 'white',
    marginRight: theme.spacing(1),
  },
  supportText: {
    color: 'white',
    fontWeight: '500',
  },
  supportTitle: {
    color: theme.palette.text.primary,
    fontWeight: '600',
    marginBottom: theme.spacing(1),
  },
  changeDurationContainer: {
    background: 'linear-gradient(135deg, #fff3e0 0%, #ffccbc 100%)',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    border: '1px solid #ffcc02',
  },
  changeDurationButton: {
    background: 'linear-gradient(45deg, #FF9800 30%, #F57C00 90%)',
    color: 'white',
    borderRadius: 20,
    padding: theme.spacing(1, 2),
    fontSize: '0.9rem',
    fontWeight: '500',
    textTransform: 'none',
    boxShadow: '0 2px 4px rgba(255, 152, 0, 0.3)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      background: 'linear-gradient(45deg, #F57C00 30%, #E65100 90%)',
      boxShadow: '0 4px 8px rgba(255, 152, 0, 0.4)',
      transform: 'translateY(-1px)',
    },
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: '95%',
  },
  loader: {
    margin: '0 auto',
  },
  currentPriceCard: {
    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
    marginBottom: theme.spacing(2),
  },
}));

type Props = {
  isRetailClient: boolean,
  match?: any,
};

const billingData = 'billing-data';
const paymentData = 'payment-data';
const baseUrl = process.env.PUBLIC_URL || '';

const getExpandedKey = (process, user) => {
  if (!!process) {
    if (!process.hasBilling()) {
      return billingData;
    } else {
      return paymentData;
    }
  }
};

const PaymentRenovation = (props: Props) => {
  const { user, doGet, doPost } = useContext(AuthContext);
  const { t } = useTranslation();
  const history = useHistory();
  const { match, isRetailClient } = props;
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [supportPhone, setSupportPhone] = useState<?string>();
  const [agentPhone, setAgentPhone] = useState<?string>();
  const [process, setProcess] = useState<?RenewalSignProcess>();
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<?string>();
  const [expanded, setExpanded] = useState<?string>();
  const [showDurationDialog, setShowDurationDialog] = useState<boolean>(false);
  const [selectedPriceCode, setSelectedPriceCode] = useState<string>('');
  const [priceError, setPriceError] = useState<string>('');
  const [config, setConfig] = useState();
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const expandedKey = getExpandedKey(process, user);
    setExpanded(expandedKey);
  }, [process, user]);

  const handleExpanded = (key: string) => (event, isExpanded: boolean) => {
    setExpanded(isExpanded ? key : null);
  };

  const loadData = () => {
    if (!process && match && match.params && match.params.code && match.params.token) {
      let path = 'retail';
      setAuthToken(null);
      if (!isRetailClient) {
        setAuthToken(match.params.token);
        path = 'renewal';
      }

      // Load configuration first, then load process
      const loadConfig = () => {
        return doGet('/customer/sign-process-config').then((response: ApiResponse) => {
          if (response.ok) {
            const configData = SignProcessConfig.parse(response.data);
            setConfig(configData);
            return configData;
          } else if (!response.is401Error()) {
            enqueueSnackbar(t(response.message), {
              variant: 'error',
            });
          }
          return null;
        });
      };

      // Load process data
      const loadProcess = (configData) => {
        return doGet(`/customer/${path}-sign-process/${match.params.code}/${match.params.token}`).then(
          (response: ApiResponse) => {
            if (response.ok) {
              const theProcess = RenewalSignProcess.parse(response.data, configData);
              setProcess(theProcess);
              const expandedKey = getExpandedKey(theProcess, user);
              setExpanded(expandedKey);
            } else {
              setError(response.message || 'Error');
            }
          }
        );
      };

      // Execute in sequence: config first, then process
      if (!config) {
        loadConfig().then((configData) => {
          if (configData) {
            loadProcess(configData);
          }
        });
      } else {
        loadProcess(config);
      }

      // Load support phones
      if (!supportPhone) {
        doGet('/customer/support-phone').then((response: ApiResponse) => setSupportPhone(response.data));
      }
      if (!agentPhone) {
        doGet('/customer/agent-phone').then((response: ApiResponse) => setAgentPhone(response.data));
      }
    }
  };

  useEffect(loadData, []);

  const savePaymentData = (paymentInfo) => {
    if (!!process) {
      setProcessing(true);
      doPost(`/customer/sign-process/${process.code}/payment`, paymentInfo).then((response: ApiResponse) => {
        if (response.ok) {
          let theProcess = RenewalSignProcess.parse(response.data, config);
          setProcess(theProcess);
          const expandedKey = getExpandedKey(theProcess, user);
          setExpanded(expandedKey);
          enqueueSnackbar(t('Payment renewal data saved'), {
            variant: 'success',
          });
        } else if (!response.is401Error()) {
          enqueueSnackbar(t(response.message), {
            variant: 'error',
          });
        }
        setProcessing(false);
      });
    }
  };

  const applyCoupon = (couponCode) => {
    if (!!process) {
      setProcessing(true);
      doPost(`/customer/sign-process/${process.code}/coupon`, { code: couponCode }).then((response: ApiResponse) => {
        if (response.ok) {
          let theProcess = RenewalSignProcess.parse(response.data, config);
          setProcess(theProcess);
          enqueueSnackbar(t('Renewal coupon applied'), {
            variant: 'success',
          });
        } else if (!response.is401Error()) {
          enqueueSnackbar(t(response.message), {
            variant: 'error',
          });
        }
        setProcessing(false);
      });
    }
  };

  const saveBillingData = (data: PaymentProps): Promise<boolean> => {
    if (!!process) {
      setProcessing(true);

      return new Promise<boolean>((resolve) => {
        doPost(`/customer/renewal-sign-process/${process.code}/update-billing`, data).then((response: ApiResponse) => {
          if (response.ok && !!response.data) {
            let theProcess = RenewalSignProcess.parse(response.data, config);
            setProcess(theProcess);
            enqueueSnackbar(t('Billing data for renewal saved'), {
              variant: 'success',
            });
          } else if (!response.is401Error()) {
            enqueueSnackbar(t(response.message), {
              variant: 'error',
            });
          }
          setProcessing(false);
          resolve(response.ok);
        });
      });
    }
    return new Promise<boolean>((resolve) => resolve(false));
  };

  const refreshProcess = () => {
    if (match && match.params && match.params.code && match.params.token) {
      let path = isRetailClient ? 'retail' : 'renewal';
      setRefreshing(true);
      doGet(`/customer/${path}-sign-process/${match.params.code}/${match.params.token}`)
        .then((response: ApiResponse) => {
          if (response.ok) {
            const theProcess = RenewalSignProcess.parse(response.data);
            setProcess(theProcess);
            const expandedKey = getExpandedKey(theProcess, user);
            setExpanded(expandedKey);
            enqueueSnackbar(t('Información Actualizada'), { variant: 'success' });
          } else {
            enqueueSnackbar(t(response.message || 'Error'), { variant: 'error' });
          }
        })
        .finally(() => setRefreshing(false));
    }
  };

  const handleChangeDuration = () => {
    setSelectedPriceCode(process?.priceCode || '');
    setShowDurationDialog(true);
    setPriceError('');
  };

  const handleDurationDialogClose = () => {
    if (processing) return;
    setShowDurationDialog(false);
    setSelectedPriceCode('');
    setPriceError('');
  };

  const handlePriceChange = (event) => {
    setSelectedPriceCode(event.target.value || '');
    setPriceError('');
  };

  const handleDurationUpdate = () => {
    if (processing) return;

    setPriceError(!selectedPriceCode ? 'Required' : '');

    if (selectedPriceCode && process) {
      setProcessing(true);

      doPost(`/customer/renewal-sign-process/${process.code}/update-duration/${selectedPriceCode}`).then(
        (response: ApiResponse) => {
          setProcessing(false);
          if (response.ok) {
            const updatedProcess = RenewalSignProcess.parse(response.data, config);
            setProcess(updatedProcess);
            setShowDurationDialog(false);

            // Force refresh of expanded state to trigger re-render of payment component
            const expandedKey = getExpandedKey(updatedProcess, user);
            setExpanded(null); // Collapse first
            setTimeout(() => {
              setExpanded(expandedKey); // Then expand to force refresh
            }, 100);

            enqueueSnackbar(t('Duration updated successfully'), {
              variant: 'success',
            });
          } else if (!response.is401Error()) {
            enqueueSnackbar(t(response.message), {
              variant: 'error',
            });
          }
        }
      );
    }
  };

  if (!match || !match.params || !match.params.code) {
    return <Redirect to={routes.home} />;
  }

  return (
    <Container component="main" maxWidth="xl">
      <CssBaseline />
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Box className={classes.appBarContent}>
            <div className={classes.brandContainer}>
              <img src={`${baseUrl}/images/logo.png`} alt="Logo" className={classes.logo} />
              <div>
                <Typography variant="h6" className={classes.brandText}>
                  {t('Payment Renewal Validation')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t('EcuaFirma Digital Signature Renewal Process')}
                </Typography>
              </div>
            </div>
            <Typography variant="caption" className={classes.poweredBy}>
              {t('Powered by eQon')}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <div className={classes.paper}>
        {!process && !error && [1, 2, 3, 4, 5].map((index) => <Skeleton key={index} />)}
        {!!error && (
          <>
            <Typography variant="subtitle2" className={classes.error}>
              {t(error)}
            </Typography>
          </>
        )}
        {!!process && (
          <>
            <Typography variant="h5">
              {process.isDraft() && <DraftIcon className={classes.draft} />}
              {!process.isProcessing() && !process.isCompleted() && <ValidatingIcon className={classes.draft} />}
              {(process.isProcessing() || process.isCompleted()) && <ProcessingIcon className={classes.processing} />}
              &nbsp;{t(toSentence(process.processType))} <small>({t(`Your process is ${process.state}`)})</small>
            </Typography>
            <Typography variant="subtitle2" className={classes.grey}>
              {t('Duration')}: {process.daysDuration} {t(process.valueDuration).toLowerCase()}.
            </Typography>

            {!!process.containerTitle && (
              <Typography variant="subtitle2" className={classes.grey}>
                {t('Container')}: {process.containerTitle} ${process.containerPrice.toFixed(2)}.
              </Typography>
            )}
            <Typography variant="subtitle2" className={classes.grey}>
              {t('Total price')}: ${process.price.toFixed(2)}.
            </Typography>
            <small>&nbsp;</small>

            {/* Duration Change Option */}
            {!!process && config && (
              <Box className={classes.changeDurationContainer}>
                <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 600 }}>
                  {t('Want to change duration?')}
                </Typography>
                <Button
                  startIcon={<EditIcon />}
                  className={classes.changeDurationButton}
                  onClick={handleChangeDuration}
                  size="small">
                  {t('Change Duration')}
                </Button>
              </Box>
            )}

            {/* Billing data for non-retail users */}
            {!!process && !!process.hasDataLoaded() && !!user && !user.isRetail && (
              <ProcessBillingData
                code={billingData}
                disabled={processing}
                onSave={saveBillingData}
                expanded={expanded === billingData}
                onExpanded={handleExpanded}
                renewal={true}
                process={process}
              />
            )}

            {/* Payment data */}
            {!!process && process.state !== 'DISCARDED' ? (
              <ProcessRenewalPaymentData
                code={paymentData}
                loading={processing}
                disabled={processing || !process.hasBilling()}
                onSave={savePaymentData}
                onApplyCoupon={applyCoupon}
                expanded={expanded === paymentData}
                onExpanded={handleExpanded}
                process={process}
                priceCode={process.priceCode} // Pass priceCode as prop to trigger re-render
                onRefreshProcess={refreshProcess}
                refreshLoading={refreshing}
              />
            ) : (
              <Grid item xs={12} className={classes.fadeInAnimation}>
                <div className={classes.discardedCard}>
                  <div className={classes.verificationContent}>
                    <CancelIcon className={classes.discardedIcon} />
                    <Typography variant="h5" className={classes.discardedTitle}>
                      {t('Renewal Discarded')}
                    </Typography>

                    <Typography variant="body1" className={classes.discardedDescription}>
                      {t('The renewal process has been discarded and no changes were applied')}
                    </Typography>

                    <div className={classes.discardedBadge}>
                      <ErrorIcon fontSize="small" />
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {t('Discarded')}
                      </Typography>
                    </div>
                  </div>
                </div>
              </Grid>
            )}

            {/* Continue Process Button */}
            {!!process && !!process.nextProcessCode && process.hasPaymentProcess && (
              <Box className={classes.bottomButton}>
                <div className={classes.nextStepContainer}>
                  <Typography variant="h6" className={classes.nextStepText}>
                    {t('Ready to continue?')}
                  </Typography>
                  <Typography variant="body2" className={classes.nextStepSubtext}>
                    {t('Complete your digital signature process in the next step')}
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<NextIcon />}
                    className={`${classes.nextStepButton} ${classes.pulseAnimation}`}
                    onClick={() => {
                      history.push(`/process/${process?.nextProcessCode || ''}`);
                    }}>
                    {t('Continue Process')}
                  </Button>
                </div>
              </Box>
            )}
          </>
        )}
      </div>

      {/* Agent Support */}
      {agentPhone && user ? (
        <Box className={classes.supportContainer}>
          <Typography variant="subtitle1" className={classes.supportTitle}>
            {t('Need agent assistance?')}
          </Typography>
          <Button
            fullWidth
            startIcon={<SupportIcon className={classes.supportIcon} />}
            className={classes.supportButton}
            onClick={() => {
              const url = whatsappURL(agentPhone, t('Agent renewal support text', user));
              window.open(url, '_blank');
            }}>
            <span className={classes.supportText}>{t('Contact Agent for Renewal via WhatsApp')}</span>
          </Button>
        </Box>
      ) : (
        supportPhone &&
        user && (
          <Box className={classes.supportContainer}>
            <Typography variant="subtitle1" className={classes.supportTitle}>
              {t('Need technical support?')}
            </Typography>
            <Button
              fullWidth
              startIcon={<SupportIcon className={classes.supportIcon} />}
              className={classes.supportButton}
              onClick={() => {
                const url = whatsappURL(supportPhone, t('Renewal support text', user));
                window.open(url, '_blank');
              }}>
              <span className={classes.supportText}>{t('Contact Renewal Support via WhatsApp')}</span>
            </Button>
          </Box>
        )
      )}

      {/* Duration Change Dialog */}
      <Dialog
        disableEscapeKeyDown
        open={showDurationDialog}
        onClose={handleDurationDialogClose}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('Change Sign Duration')}</DialogTitle>
        <DialogContent>
          {!!process && (
            <Card className={classes.currentPriceCard}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('Current Duration')}
                </Typography>
                <Typography variant="h6">
                  {process.daysDuration} {t(process.valueDuration).toLowerCase()}: ${process.price.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          )}

          {processing && <CircularProgress className={classes.loader} size="1.5em" />}

          {config && process && (
            <FormControl className={classes.formControl} disabled={processing}>
              <InputLabel id="newDurationLabel">{t('New Sign Duration')}</InputLabel>
              <Select
                labelId="priceLabel"
                id="priceSelect"
                value={selectedPriceCode}
                onChange={handlePriceChange}
                input={<Input />}>
                {config.processTypes[process.processType].map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.durationValue()} {t(item.durationLabel()).toLowerCase()}: ${item.price.toFixed(2)}
                  </MenuItem>
                ))}
              </Select>
              {priceError && <span className={classes.error}>{t(priceError)}</span>}
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={processing} onClick={handleDurationUpdate} variant="contained" color="secondary">
            {t('Update Duration')}
          </Button>
          <Button disabled={processing} onClick={handleDurationDialogClose} variant="contained" color="primary">
            {t('Cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PaymentRenovation;
